import React from "react";
import { useBluelagoon } from "@bluelagoon";
import { Button } from "@shadcn-in-nx/ui/button";
import { Copy, Database } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@shadcn-in-nx/ui/dialog";
import { Input } from "@shadcn-in-nx/ui/input";
import { Label } from "@shadcn-in-nx/ui/label";
import { Textarea } from "@shadcn-in-nx/ui/textarea";

function CloneDatabaseModal({
    onSuccess,
    databaseToClone,
    open,
    onOpenChange,
}) {
    const { t } = useTranslation();
    const [newDatabase, setNewDatabase] = React.useState({
        name: "",
        description: "",
    });
    const [isCloning, setIsCloning] = React.useState(false);
    const [error, setError] = React.useState("");
    const [progress, setProgress] = React.useState(0);

    const { call: cloneDatabaseCall } = useBluelagoon({
        method: "cloneDatabase",
    });

    React.useEffect(() => {
        if (databaseToClone) {
            setNewDatabase({
                name: "",
                description: `Clone of ${databaseToClone.name}`,
            });
        }
    }, [databaseToClone]);

    const handleCloneDatabase = async () => {
        if (!newDatabase.name.trim() || !databaseToClone) return;

        setIsCloning(true);
        setError("");
        setProgress(0);

        try {
            // Calculate estimated time based on data size
            const totalRecords =
                databaseToClone.tables?.reduce(
                    (sum, table) => sum + (table.records?.length || 0),
                    0
                ) || 0;
            const estimatedTimeMs = Math.max(2000, totalRecords * 10); // Base 2 seconds + 10ms per record

            // Start progress simulation
            const progressInterval = setInterval(() => {
                setProgress((prev) => {
                    if (prev >= 90) return prev; // Don't go to 100% until complete
                    return prev + Math.random() * 15; // Increment by 0-15%
                });
            }, 500);

            // Show initial progress
            setProgress(10);

            const response = await cloneDatabaseCall({
                name: newDatabase.name.replace(/\s/g, ""),
                description: newDatabase.description.trim(),
                sourceDatabaseId: databaseToClone.id,
            });

            // Clear progress interval and show completion
            clearInterval(progressInterval);
            setProgress(100);

            if (response?.success === false) {
                throw new Error(
                    response.data?.error ||
                        response.error ||
                        "Failed to clone database"
                );
            }

            // Reset form and close modal
            setNewDatabase({ name: "", description: "" });
            onOpenChange(false);

            // Call the onSuccess callback if provided
            if (onSuccess) {
                onSuccess();
            }
        } catch (error) {
            console.error("Failed to clone database:", error);
            setError(
                error.data?.error ||
                    error.error ||
                    error.message ||
                    error ||
                    "Failed to clone database"
            );
        } finally {
            setIsCloning(false);
            setProgress(0);
        }
    };

    const handleModalOpenChange = (open) => {
        onOpenChange(open);
        if (!open) {
            setNewDatabase({ name: "", description: "" });
            setError("");
            setProgress(0);
        }
    };

    if (!databaseToClone) {
        return null;
    }

    return (
        <Dialog open={open} onOpenChange={handleModalOpenChange}>
            <DialogContent className="sm:max-w-[425px] max-h-[80vh] flex flex-col">
                <DialogHeader className="flex-shrink-0">
                    <DialogTitle className="flex items-center gap-2">
                        <Copy className="w-5 h-5" />
                        {t("databases.cloneDatabase")}
                    </DialogTitle>
                    <DialogDescription>
                        {t("databases.cloneDatabaseDescription", {
                            name: databaseToClone.name,
                        })}
                        <br />
                        <span className="text-amber-600 font-medium">
                            ⚠️ Large databases may take several minutes to
                            clone.
                        </span>
                    </DialogDescription>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto min-h-0">
                    <div className="grid gap-4 py-4">
                        <div className="grid gap-2">
                            <Label htmlFor="database-name">
                                {t("databases.databaseName")}
                            </Label>
                            <Input
                                id="database-name"
                                value={newDatabase.name}
                                onChange={(e) =>
                                    setNewDatabase((prev) => ({
                                        ...prev,
                                        name: e.target.value,
                                    }))
                                }
                                placeholder={`e.g., ${databaseToClone.name}_copy, ${databaseToClone.name}_v2`}
                            />
                        </div>

                        <div className="grid gap-2">
                            <Label htmlFor="database-description">
                                {t("databases.description")}
                            </Label>
                            <Textarea
                                id="database-description"
                                value={newDatabase.description}
                                onChange={(e) =>
                                    setNewDatabase((prev) => ({
                                        ...prev,
                                        description: e.target.value,
                                    }))
                                }
                                placeholder={t("databases.enterDescription")}
                                rows={3}
                            />
                        </div>

                        {databaseToClone.tables && (
                            <div className="bg-gray-50 p-3 rounded-md">
                                <div className="text-sm text-gray-600">
                                    <div className="font-medium mb-1">
                                        Database Contents:
                                    </div>
                                    <div>
                                        • {databaseToClone.tables.length} table
                                        {databaseToClone.tables.length !== 1
                                            ? "s"
                                            : ""}
                                    </div>
                                    {databaseToClone.tables.map((table) => (
                                        <div
                                            key={table.id}
                                            className="ml-4 text-gray-500"
                                        >
                                            - {table.name}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {isCloning && progress > 0 && (
                            <div className="grid gap-2">
                                <div className="flex justify-between text-sm text-gray-600">
                                    <span>{t("databases.cloning")}</span>
                                    <span>{progress}%</span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-2">
                                    <div
                                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                        style={{ width: `${progress}%` }}
                                    ></div>
                                </div>
                            </div>
                        )}

                        {error && (
                            <div className="text-sm text-red-600 bg-red-50 p-3 rounded-md">
                                {error}
                            </div>
                        )}
                    </div>
                </div>

                <DialogFooter className="flex-shrink-0 mt-4">
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={isCloning}
                    >
                        {t("common.cancel")}
                    </Button>
                    <Button
                        onClick={handleCloneDatabase}
                        disabled={!newDatabase.name.trim() || isCloning}
                        className="btn btn-primary"
                    >
                        {isCloning ? (
                            <div className="flex items-center gap-2">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                {t("databases.cloning")}
                            </div>
                        ) : (
                            <div className="flex items-center gap-2">
                                <Copy className="w-4 h-4" />
                                {t("databases.cloneDatabase")}
                            </div>
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default CloneDatabaseModal;
